import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Dialog, { type DialogProps } from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import { Breakpoint, SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type {
	ComponentPropsWithRef,
	FormEventHandler,
	ReactNode,
	Ref,
	UIEvent
} from 'react';
import { useState } from 'react';

import { mergeSx } from '@/utils/mergeSx';

import { modalStyles as sx } from './Modal.styles';

export type Props = Omit<DialogProps, 'title' | 'onSubmit'> & {
	title: ReactNode;
	titleAdornment?: ReactNode;
	header?: ReactNode;
	children: ReactNode;
	footer?: ReactNode;
	footerSpacing?: boolean;
	size?: Breakpoint;
	fullHeight?: boolean;
	hideCloseButton?: boolean;
	onSubmit?: FormEventHandler<HTMLFormElement>;
	bodyRef?: Ref<HTMLDivElement>;
	customSx?: {
		body?: SxProps<Theme>;
		header?: SxProps<Theme>;
		footer?: SxProps<Theme>;
	};
};

export const Modal = ({
	open,
	onClose,
	title,
	titleAdornment,
	header,
	children,
	footer,
	footerSpacing = true,
	hideCloseButton = false,
	size = 'sm',
	fullHeight = false,
	onSubmit,
	bodyRef,
	disableEscapeKeyDown,
	customSx,
	...dialogProps
}: Props) => {
	const [scrolled, setScrolled] = useState(false);
	const [atBottom, setAtBottom] = useState(false);

	const handleCloseClick = () => {
		if (onClose) {
			onClose({}, 'escapeKeyDown');
		}
	};

	const handleBodyScroll = (event: UIEvent<HTMLDivElement>) => {
		const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
		setScrolled(scrollTop > 0);
		setAtBottom(scrollTop + clientHeight >= scrollHeight - 1);
	};

	const paperProps = onSubmit
		? ({
				component: 'form',
				onSubmit,
				noValidate: true,
				sx: sx.paper(fullHeight)
			} as ComponentPropsWithRef<'form'> & { sx: SxProps<Theme> })
		: { sx: sx.paper(fullHeight) };

	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullWidth
			maxWidth={size}
			disableEscapeKeyDown={disableEscapeKeyDown}
			aria-labelledby='modal-title'
			aria-describedby='modal-description'
			slotProps={{ paper: paperProps }}
			{...dialogProps}
		>
			<DialogTitle sx={sx.title} id='modal-title' component='div'>
				<Box sx={sx.titleRow}>
					<Box sx={sx.titleSlot}>
						{typeof title === 'string' ? (
							<Typography variant='h6' component='h2' fontWeight={700}>
								{title}
							</Typography>
						) : (
							title
						)}
					</Box>
					{(titleAdornment || !hideCloseButton) && (
						<Box sx={sx.titleEnd}>
							{titleAdornment}
							{!hideCloseButton && (
								<IconButton
									onClick={handleCloseClick}
									aria-label='Close dialog'
								>
									<CloseIcon />
								</IconButton>
							)}
						</Box>
					)}
				</Box>
			</DialogTitle>

			{header && (
				<Box sx={mergeSx(sx.header(scrolled), customSx?.header)}>{header}</Box>
			)}

			<DialogContent
				ref={bodyRef}
				onScroll={handleBodyScroll}
				sx={mergeSx(sx.body(!!footer), customSx?.body)}
				id='modal-description'
			>
				{children}
			</DialogContent>

			{footer && (
				<DialogActions
					sx={mergeSx(sx.footer(atBottom, footerSpacing), customSx?.footer)}
				>
					{footer}
				</DialogActions>
			)}
		</Dialog>
	);
};
